import type {
  AutoCatchUpLot,
  AutoCatchUpSweep,
  AutoCatchUpSweepSchoolStatus,
  AutoCatchUpSweepStatus,
  AutoCatchUpSweepTrigger,
} from '../types';

/**
 * Vocabulaire commun aux deux vues du rattrapage automatique (liste des passes
 * et vue par école) : mêmes libellés, mêmes couleurs des deux côtés.
 */

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'hier';
  if (days < 7) return `il y a ${days} j`;
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds < 0) return '—';
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${String(minutes % 60).padStart(2, '0')}`;
}

/**
 * « Élèves (3 ajoutés, 20 changés de classe) » : ce qu'un lot rejoué a produit.
 * Un lot de rattachement peut n'avoir déplacé que des élèves déjà migrés
 * (`reassigned`) ou débloqué des rattachements en échec (`attached`) sans rien
 * ajouter — ces compteurs sont absents des traces d'avant le 02/09/2026.
 */
export function formatLotSummary(lot: AutoCatchUpLot): string {
  const parts: string[] = [];
  const reassigned = lot.reassigned ?? 0;
  const preserved = lot.preserved ?? 0;
  const attached = lot.attached ?? 0;
  if (lot.added > 0 || (reassigned === 0 && preserved === 0 && attached === 0 && lot.failed === 0)) {
    parts.push(`${lot.added} ajouté${lot.added > 1 ? 's' : ''}`);
  }
  if (reassigned > 0) parts.push(`${reassigned} changé${reassigned > 1 ? 's' : ''} de classe`);
  if (preserved > 0) parts.push(`${preserved} à arbitrer`);
  if (attached > 0) parts.push(`${attached} rattaché${attached > 1 ? 's' : ''}`);
  if (lot.failed > 0) parts.push(`${lot.failed} en échec`);
  return `${lot.name} (${parts.join(', ')})`;
}

export function triggerLabel(trigger: AutoCatchUpSweepTrigger): string {
  switch (trigger) {
    case 'cron':
      return 'Tâche programmée';
    case 'manual':
      return "Depuis l'interface";
    default:
      return 'Ligne de commande';
  }
}

/**
 * Une passe restée « en cours » sans signe de vie a été coupée en vol : on ne
 * la présente pas comme active, ce serait mentir sur l'état du système.
 */
export function sweepStatusPresentation(
  status: AutoCatchUpSweepStatus,
  interrupted = false
): { label: string; className: string } {
  if (interrupted) {
    return {
      label: 'Interrompue',
      className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    };
  }
  switch (status) {
    case 'running':
      return {
        label: 'En cours',
        className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
      };
    case 'completed':
      return {
        label: 'Terminée',
        className: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
      };
    case 'partial':
      return {
        label: 'Avec incidents',
        className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
      };
    case 'skipped_locked':
      return {
        label: 'Ignorée (déjà en cours)',
        className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
      };
    default:
      return {
        label: 'En échec',
        className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
      };
  }
}

export function schoolOutcomePresentation(status: AutoCatchUpSweepSchoolStatus): {
  label: string;
  className: string;
} {
  switch (status) {
    case 'added':
      return {
        label: 'Éléments repris',
        className: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
      };
    case 'partial':
      return {
        label: 'Repris avec échecs',
        className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
      };
    case 'failed':
      return {
        label: 'Échec',
        className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
      };
    case 'error':
      return {
        label: 'Erreur technique',
        className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
      };
    case 'pending':
      return {
        label: 'Écarts détectés (simulation)',
        className: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
      };
    default:
      return {
        label: 'Rien à reprendre',
        className: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
      };
  }
}

/** Périmètre d'une passe : toutes les écoles éligibles, ou un dossier ciblé. */
export function scopeLabel(sweep: AutoCatchUpSweep): string {
  if (sweep.scope === 'all') return 'Toutes les écoles';
  return sweep.scopeLabel ? `Ciblée · ${sweep.scopeLabel}` : 'Ciblée';
}
